import { useEffect, useRef } from 'react';
import { useUser } from '@clerk/clerk-react';
import { analyticsClient } from '../analytics/analyticsClient';
import { sentryClient } from '../analytics/sentryClient';

/**
 * Syncs the current Clerk user identity to Sentry and PostHog.
 *
 * - Sentry: `setUser({ id })` only — no email/PII per security hardening.
 * - PostHog: `identify(id, { tier })` on sign-in, `reset()` only when
 *   transitioning from signed-in to signed-out (so anonymous waitlist
 *   identify is not wiped on landing). Do not call `reset()` on waitlist
 *   `identify` — hero A/B assignment must stay on the same distinct_id.
 *
 * Mount once near the root, inside ClerkProvider.
 */
export function AnalyticsAuthSync() {
  const { isSignedIn, user } = useUser();
  const wasSignedIn = useRef(false);

  useEffect(() => {
    if (isSignedIn && user) {
      wasSignedIn.current = true;
      sentryClient.setUser({ id: user.id });

      const tier = (user.publicMetadata?.tier as string | undefined) ?? undefined;
      analyticsClient.identify(user.id, tier ? { tier } : undefined);
    } else if (wasSignedIn.current) {
      wasSignedIn.current = false;
      sentryClient.setUser(null);
      analyticsClient.reset();
    }
  }, [isSignedIn, user]);

  return null;
}

export default AnalyticsAuthSync;
